<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import PillBase from "../pills/pillBase.vue";

const emit = defineEmits(['navigate:create-competition']);
const competitions = ref([]);
const loading = ref(true);

const PRO_MONTH_VALUE = 149;

const fetchCompetitions = async () => {
    try {
        loading.value = true;
        const response = await axios.get(route('business-partner.competitions.index'));
        competitions.value = response.data.competitions;
    } catch (error) {
        console.error('Error fetching competitions:', error);
    } finally {
        loading.value = false;
    }
};

const getDaysLeft = (competition) => {
    const today = new Date();
    const endDate = new Date(competition.end_date);
    const timeDiff = endDate - today;
    const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return daysLeft > 0 ? daysLeft : 0;
};

const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

const calculatePrizeValue = (prize) => {
    const proMonthsValue = prize.proMonths * PRO_MONTH_VALUE;
    const benefitsValue = prize.benefits.reduce((total, benefit) => {
        return total + (Number(benefit.value) || 0);
    }, 0);
    return proMonthsValue + benefitsValue;
};

const calculateTotalPrizeValue = (prizes) => {
    return prizes.reduce((total, prize) => {
        return total + calculatePrizeValue(prize);
    }, 0);
};

const getStatusText = (competition) => {
    const daysLeft = getDaysLeft(competition);
    if (daysLeft > 0) {
        return 'Active';
    } else {
        return 'Ended';
    }
};

const getStatusClass = (competition) => {
    const daysLeft = getDaysLeft(competition);
    if (daysLeft > 0) {
        return 'bg-green-100 text-green-800';
    } else {
        return 'bg-gray-100 text-gray-800';
    }
};

onMounted(fetchCompetitions);
</script>

<template>
    <div class="competition-list">
        <div v-if="loading" class="py-8 text-center">
            <i class="text-3xl fa-light fa-spinner fa-spin"></i>
        </div>
        <div v-else-if="competitions.length === 0" class="py-8 text-center">
            <p class="text-gray-500">No giveaways found. Create your first giveaway to get started.</p>
            <button-secondary
                @click="emit('navigate:tab', 'create-competition')"
            >
                Create giveaway
            </button-secondary>
        </div>
        <div v-else class="space-y-6">
            <div v-for="competition in competitions" :key="competition.id" class="pb-6 border-b border-gray-200">
                <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <!-- Competition Info -->
                    <div class="md:col-span-2">
                        <div class="flex items-start justify-between">
                            <div>
                                <div class="mb-4 text-sm text-gray-600">
                                    <div>Created {{ formatDate(competition.created_at) }}</div>
                                    <div v-if="getDaysLeft(competition) > 0">Ends at {{ formatDate(competition.end_date) }}
                                        <span
                                            class="ml-2 font-bold"
                                        >
                                            {{ getDaysLeft(competition) }} {{ getDaysLeft(competition) === 1 ? 'day' : 'days' }} left
                                        </span>
                                    </div>
                                    <div v-else>
                                        Ended at {{ formatDate(competition.end_date) }}
                                        <div class="font-bold">
                                            Giveaway ended
                                        </div>
                                    </div>

                                </div>
                            </div>
                            <div class="flex items-center">
                                <pill :class="getStatusClass(competition)">
                                    {{ getStatusText(competition) }}
                                </pill>
                            </div>
                        </div>

                        <div>
                            <div class="mb-2 text-sm text-gray-500">Social Media Platforms</div>
                            <div class="flex flex-wrap gap-2">
                                <span v-for="(profile, index) in competition.social_media_profiles" :key="index"
                                    class="px-2 py-1 text-xs bg-gray-100 rounded-full">
                                    <i :class="profile.icon" class="mr-1"></i>
                                    {{ profile.name }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <card-sub>
                        <div>
                            <div class="text-gray-500">Prize Value</div>
                            <div class="text-xl font-semibold">
                                €{{ calculateTotalPrizeValue(competition.prizes).toFixed(2) }}
                            </div>
                        </div>
                        <div>
                            <div class="text-gray-500">Generated Leads</div>
                            <div class="text-xl font-semibold">
                                {{ competition.entries_count || 0 }}
                            </div>
                        </div>
                    </card-sub>
                </div>

                <div class="my-4" v-if="competition.winners.length === 0">
                    <div class="mb-2 text-sm text-gray-500">Prizes</div>
                    <div class="flex flex-wrap -mr-2">
                        <div
                            v-for="(prize, prizeIndex) in competition.prizes"
                            :key="prizeIndex"
                            class="flex items-stretch w-full pb-2 pr-2 lg:w-1/3 justify-stretch"
                        >
                            <card-sub class="w-full">
                                <div class="flex justify-between">
                                    <span class="font-semibold">Prize {{ prizeIndex + 1 }}</span>
                                    <pill class="text-black bg-gray-200">
                                        {{ prize.winners }} winner{{ prize.winners > 1 ? 's' : '' }}
                                    </pill>
                                </div>
                                <div class="mt-2 space-x-1 text-xs">
                                    <span>{{ prize.proMonths }} Pro month{{ prize.proMonths > 1 ? 's' : '' }}</span>
                                    <span>•</span>
                                    <span>Value: €{{ calculatePrizeValue(prize).toFixed(2) }}</span>
                                </div>
                                <div
                                    class="text-xs"
                                    v-if="prize.benefits.length > 0"
                                >
                                    <span>{{ prize.benefits.length }} Benefits:</span>
                                    <ul class="pl-3 list-disc">
                                        <li
                                            v-for="(benefit, benefitIndex) in prize.benefits"
                                            :key="benefitIndex"
                                        >
                                            {{ benefit.description }}
                                        </li>
                                    </ul>
                                </div>
                            </card-sub>
                        </div>
                    </div>
                </div>

                <div
                    class="flex justify-center pt-4 mt-4 lg:mt-6 lg:pt-6"
                    v-if="competition.winners.length === 0"
                >
                    <div class="text-center">
                        <a :href="route('giveaway.participate', competition.uuid)"
                            target="_blank"
                        >
                            <button-primary
                                size="lg"
                            >
                                Visit Giveaway page
                            </button-primary>
                        </a>
                        <div class="mt-4">
                            <p class="mb-1">Or share this link:</p>
                            <copyable-text
                                text-class="text-xxs md:text-sm"
                                :copyable-text="route('giveaway.participate', competition.uuid)"
                            ></copyable-text>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div class="my-5 mb-2 text-sm text-gray-500">Winners</div>
                    <div class="flex flex-wrap -mr-2">
                        <div
                            v-for="winner in competition.winners"
                            :key="winner.id"
                            class="flex items-stretch w-full pb-2 pr-2 lg:w-1/3 justify-stretch"
                        >
                            <card-sub class="w-full" :class="{ 'opacity-50': !winner.participant }">
                                <div class="flex justify-between">
                                    <span class="font-semibold">
                                        {{ winner.participant?.name ?? 'Too few entries' }}<br>
                                        <span class="text-xs text-gray-500">
                                            {{ winner.participant?.email }}
                                        </span>
                                    </span>
                                </div>
                                <div class="mt-2 space-x-1 text-xs">
                                    <span>{{ winner.prize.proMonths }} Pro month{{ winner.prize.proMonths > 1 ? 's' : '' }}</span>
                                    <span>•</span>
                                    <span>Value: €{{ calculatePrizeValue(winner.prize).toFixed(2) }}</span>
                                </div>
                                <div
                                    class="text-xs"
                                    v-if="winner.prize.benefits.length > 0"
                                >
                                    <span>{{ winner.prize.benefits.length }} Benefits:</span>
                                    <ul class="pl-3 list-disc">
                                        <li
                                            v-for="(benefit, benefitIndex) in winner.prize.benefits"
                                            :key="benefitIndex"
                                        >
                                            {{ benefit.description }}
                                        </li>
                                    </ul>
                                </div>

                                <div v-if="winner.participant">
                                    <hr class="my-2">

                                    <div v-if="winner.pro_sent">
                                        <div class="mt-2 space-x-1 text-green-500">
                                            <span>Pro months delivered</span>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <div class="mt-2 space-x-1 text-red-500">
                                            <span>Pro months not delivered</span>
                                        </div>
                                        <div class="mt-2 space-x-1 italic">
                                            <span>Please contact the support team to deliver the pro months.</span>
                                        </div>
                                    </div>

                                    <hr class="my-2">

                                    <div v-if="winner.bonuses_sent">
                                        <div class="mt-2 space-x-1 text-green-500">
                                            <span>Bonuses delivered</span>
                                        </div>
                                    </div>
                                    <div v-else>
                                        <div class="mt-2 space-x-1 text-red-500">
                                            <span class="mb-2">Bonuses not delivered</span>
                                            <a :href="route('business-partner.competitions.send-bonuses', winner.id)" class="mt-2">
                                                <button-primary size="sm" class="mt-2">
                                                    Mark bonuses as delivered
                                                </button-primary>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </card-sub>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
